import React, { useState, useEffect, useRef } from "react";
import Header from "../Header";
import Button from "../Button";
import styles from "./ReviewBankInfo.module.css";
import icon from "./Identity_Icon.svg";
import RadioButton from "../RadioButton";
import { useHistory, useParams } from "react-router-dom";
import { useAccount } from "utils/AccountInfoContext";
import {
  useConfirmBankInfo,
  useUpdateBankInfo,
  useUploadBankDoc,
  useCancelAchRequest,
} from "./useBankInfo";
import Loader from "components/Loader";
import { useTranslation } from "react-i18next";

export function ReviewBankInfo() {
  const {t} = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const { subEntityData, cachedIBOInfo, isSubEntityLoading } = useAccount();
  const pinLevelMapping = {
    2: "Platinum Settings",
    3: "Emerald Settings",
    4: "Diamond Settings",
  };

  const pinLevel = cachedIBOInfo?.PinLevel || 0;
  const headerText = pinLevelMapping[pinLevel];
  const selectedItem = subEntityData?.find(
    (item) => item.entityId === parseInt(id)
  );

  const confirmMutation = useConfirmBankInfo(id);
  const updateMutation = useUpdateBankInfo(id);
  const uploadMutation = useUploadBankDoc(id);
  const cancelAchMutation = useCancelAchRequest(id);

  const [showBankInfo, setShowBankInfo] = useState(false);
  const [showRadioButtons, setShowRadioButtons] = useState(false);
  const [selectedOption, setSelectedOption] = useState("U.S. Bank Account");
  const [showUSBankForm, setShowUSBankForm] = useState(false);
  // const [showCanadianBankForm, setShowCanadianBankForm] = useState(false);
  const [reviewInProgress, setReviewInProgress] = useState(false);
  const [showUploadInfo, setShowUploadInfo] = useState(true);
  const [bankAccountInfo, setBankAccountInfo] = useState({
    nameOnAccount: "",
    routingNumber: "",
    accountNumber: "",
    transitNumber: "",
    institutionNumber: "",
  });
  const [submittedBankInfo, setSubmittedBankInfo] = useState(null);
  const [status, setStatus] = useState("Requested");
  const [achStatus, setAchStatus] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [nameMismatchError, setNameMismatchError] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const pendingBankInfo = selectedItem?.pendingBankInfo || null;

  useEffect(() => {
    if (selectedItem) {
      const achStatusFromApi = selectedItem.achStatus;

      setAchStatus(achStatusFromApi);

      if (
        achStatusFromApi === 1 ||
        achStatusFromApi === 2 ||
        achStatusFromApi === 3 ||
        achStatusFromApi === 6 ||
        achStatusFromApi === 7
      ) {
        setShowBankInfo(true);
      }

      if (achStatusFromApi === 4) {
        setSubmittedBankInfo(true);
        setShowBankInfo(true);
      }

      if (achStatusFromApi === 5) {
        setReviewInProgress(true);
        setShowUploadInfo(false);
      }
    }
  }, [selectedItem]);

  //Handles updating the bank info by clicking next button
  const handleNextClick = () => {
    setIsLoading(true);
    setShowRadioButtons(false);
    setShowUSBankForm(false);
    // setShowCanadianBankForm(false);

    setSubmittedBankInfo({
      nameOnAccount: bankAccountInfo.nameOnAccount,
      routingNumber: bankAccountInfo.routingNumber,
      accountNumber: bankAccountInfo.accountNumber,
      transitNumber: bankAccountInfo.transitNumber,
      institutionNumber: bankAccountInfo.institutionNumber,
    });

    // setBankAccountInfo({
    //   nameOnAccount: "",
    //   routingNumber: "",
    //   accountNumber: "",
    //   transitNumber: "",
    //   institutionNumber: "",
    // });

    const countryCode = selectedOption === "U.S. Bank Account" ? "US" : "CA";
    const entityId = selectedItem.entityId;

    updateMutation.mutate(
      {
        entityId,

        updatedAch: {
          countryCode: countryCode,
          routingNumber: bankAccountInfo.routingNumber
            ? Number(bankAccountInfo.routingNumber)
            : null,
          accountName: bankAccountInfo.nameOnAccount,
          accountNumber: bankAccountInfo.accountNumber
            ? Number(bankAccountInfo.accountNumber)
            : null,
          transitNumber: bankAccountInfo.transitNumber
            ? Number(bankAccountInfo.transitNumber)
            : null,
          institutionId: bankAccountInfo.institutionNumber
            ? Number(bankAccountInfo.institutionNumber)
            : null,
        },
      },
      {
        onSuccess: () => {
          setStatus("Requested");
          setAchStatus(4);
          setReviewInProgress(true);
          setShowUploadInfo(true);
          setIsLoading(false);
        },
        onError: (error) => {
          console.error(t("Error updating bank information"), error);
        },
      }
    );
  };

  //Handles the cancel request update button
  const handleCancelRequest = () => {
    const entityId = selectedItem.entityId;
    const address = selectedItem.address;

    cancelAchMutation.mutate(
      { entityId },
      {
        onSuccess: () => {
          setShowRadioButtons(false);
          setShowUSBankForm(false);
          // setShowCanadianBankForm(false);
          setSubmittedBankInfo(null);
          setReviewInProgress(false);

          setBankAccountInfo({
            nameOnAccount: "",
            routingNumber: null,
            accountNumber: null,
            transitNumber: null,
            institutionNumber: null,
          });

          setShowBankInfo(true);

          const achStatusFromApi = selectedItem?.achStatus;
          setAchStatus(achStatusFromApi);
          if (achStatusFromApi === 5) {
            setReviewInProgress(true);
            setShowUploadInfo(false);
          }
        },
        onError: (error) => {
          console.error(t("Error canceling request"), error);
        },
      }
    );
  };

  //Triggers when cancel update bank info clicks
  const handleCancel = () => {
    setShowRadioButtons(false);
    setShowUSBankForm(false);
    // setShowCanadianBankForm(false);
    setSubmittedBankInfo(null);
    setReviewInProgress(false);

    setBankAccountInfo({
      nameOnAccount: "",
      routingNumber: null,
      accountNumber: null,
      transitNumber: null,
      institutionNumber: null,
    });

    setShowBankInfo(true);
  };

  const handleSkipClick = () => {
    history.push(`/review-w9/${selectedItem.entityId}`);
  };

  const handleHeaderClick = () => {
    history.push("/platinum-settings");
  };

  //Handles when "Bank Information is Correct" is clicked
  const handleBankConfirmClick = () => {
    if (!selectedItem) {
      console.error(t("No selected item available"));
      return;
    }
    const entityId = selectedItem.entityId;
    const bankInfo = selectedItem.bankInfo;

    if (!bankInfo || !bankInfo.nameOnAccount || !bankInfo.lastFour) {
      console.error(t("Bank information is incomplete"));
      return;
    }

    const payload = {
      bankName: bankInfo.bankName || "",
      lastFour: bankInfo.lastFour,
      nameOnAccount: bankInfo.nameOnAccount,
    };

    confirmMutation.mutate(
      { entityId, bankInfo: payload },
      {
        onSuccess: () => {
          history.push(`/review-w9/${selectedItem.entityId}`);
        },
        onError: (error) => {
          console.error(t("Error confirming bank information"), error);
        },
      }
    );
  };

  const handleOptionChange = (value) => {
    setSelectedOption(value);
    setShowUSBankForm(value === "U.S. Bank Account");
    // setShowCanadianBankForm(value === "Canadian Bank Account");
  };

  const handleUpdateBankClick = () => {
    setShowBankInfo(false);

    setShowRadioButtons(true);
    setSelectedOption("U.S. Bank Account");
    setShowUSBankForm(true);
    // setShowCanadianBankForm(false);
    setSubmittedBankInfo(null);
    setReviewInProgress(false);
  };

  const handleBankFormSubmit = (e) => {
    e.preventDefault();
    setNameTouched(true);
    if (bankAccountInfo.nameOnAccount !== selectedItem.name && !isOwner) {
      setNameMismatchError(true);
    } else {
      setNameMismatchError(false);
      handleNextClick();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBankAccountInfo((prevInfo) => ({
      ...prevInfo,
      [name]: value,
    }));
  };

  const handleCheckboxChange = () => {
    setIsOwner(!isOwner);
  };

  const fileInputRef = useRef(null);
  const handleDownloadInstructions = async () => {
    try {
      const response = await fetch(
        "https://wwgus.blob.core.windows.net/sub-entity/ACH_Instructions.pdf"
      );
      const blob = await response.blob();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "ACH_Instructions.pdf");
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      console.error(t("Error downloading the file"), error);
    }
  };

  const accountName = selectedItem?.name || "ABC Company";
  const bankInfo = selectedItem?.bankInfo;

  const lastFour = bankInfo?.lastFour;

  const handleUploadRequest = () => {
    if (status === "Requested") {
      setShowUploadInfo(true);
      setStatus("In Review");
      setReviewInProgress(true);
      fileInputRef.current.click();
    }
  };

  const convertToBase64 = async (file) => {
    return new Promise((resolve, reject) => {
      let fileReader = new FileReader();
      fileReader.onload = function () {
        return resolve(fileReader.result);
      };
      fileReader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const base64Image = await convertToBase64(file);

      const entityId = selectedItem.entityId;

      uploadMutation.mutate(
        { entityId, fileData: base64Image },
        {
          onSuccess: () => {
            setStatus("In Review");
            setReviewInProgress(true);
            setAchStatus(5);
          },
          onError: (error) => {
            console.error(t("Error uploading document"), error);
          },
        }
      );
    } catch (error) {
      console.error(t("Error converting file to base64"), error);
    }
  };

  //Disable next button
  const isNextButtonDisabled =
    (bankAccountInfo.nameOnAccount !== accountName && !isOwner) ||
    !bankAccountInfo.routingNumber ||
    !bankAccountInfo.accountNumber;

  if (isSubEntityLoading || isLoading) {
    return (
      <div>
        <Loader color={"#415364"} size={85} />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Header header={t(headerText)} onClick={handleHeaderClick} />
      <div className={styles.flexContainer}>
        <span className={styles.header}>{t("Review Bank Information")}</span>
        <Button onClick={handleSkipClick} buttonText={t("Skip")} className={styles.skipButton}/>
      </div>

      {/* Show current bank information if available */}
      {showBankInfo ? (
        <>
          <div className={styles.labelContainer}>
            {bankInfo && achStatus !== 3 && (
              <span className={styles.accountTitle}>{accountName}</span>
            )}
          </div>
          <div className={styles.bankInfoContainer}>
            <img className={styles.icon} src={icon} alt="icon" />

            <div className={styles.accountInfo}>
              {bankInfo && achStatus !== 3 ? (
                <>
                  <span className={styles.accountName}>
                    {bankInfo.nameOnAccount}
                    <span
                      className={styles.status}
                      style={{ backgroundColor: "#43BA32" }}
                    >
                      {t("Current")}
                    </span>
                  </span>
                  <div className={styles.lastFour} style={{ color: "#243746" }}>
                    <span>{t("Account ending in")} #{bankInfo.lastFour}</span>
                  </div>
                </>
              ) : (
                <>
                  <span className={styles.accountName}>
                    {t('Bank Information Not Given')}
                  </span>
                </>
              )}
            </div>
          </div>

          <div className={styles.infoSpacer} />

          {/* Show options to update or confirm bank info */}

          {bankInfo &&
            !pendingBankInfo &&
            achStatus !== 3 &&
            achStatus !== 4 &&
            achStatus !== 5 && (
              <>
                <div className={styles.buttonsContainer}>
                  <Button
                    onClick={handleBankConfirmClick}
                    buttonText={t("Bank Information is Correct")}
                    className={styles.ctrInfoButton}
                  />
                  <Button
                    onClick={handleUpdateBankClick}
                    buttonText={t("Update Bank Information")}
                    className={styles.updateButton}
                  />
                </div>
              </>
            )}

          {achStatus === 3 && (
            <div className={styles.buttonsContainer}>
              <Button
                onClick={handleUpdateBankClick}
                buttonText={t("Add New Bank Account")}
              />
            </div>
          )}

          {/* Show pending bank information only if available */}
          {pendingBankInfo && (achStatus === 4 || achStatus === 5) && (
            <div className={styles.submitContainer}>
              <div className={styles.labelContainer}>
                <span className={styles.accountTitle}>{accountName}</span>
              </div>
              <div className={styles.bankInfoContainer}>
                <img className={styles.icon} src={icon} alt="icon" />
                <div className={styles.accountInfo}>
                  <span className={styles.accountName}>
                    {pendingBankInfo.nameOnAccount}
                    <span
                      className={styles.status}
                      style={{
                        backgroundColor:
                          achStatus === 4 ? "#243746" : "#91b6bb",
                      }}
                    >
                      {achStatus === 4 ? "Requested" : "In Review"}
                    </span>
                  </span>
                  <div className={styles.lastFour} style={{ color: "#243746" }}>
                    <span>{t("Account ending in")} #{pendingBankInfo.lastFour}</span>
                  </div>
                </div>
              </div>

              {/* Show upload and cancel buttons */}
              {achStatus === 4 && (
                <>
                  <span className={styles.voidedInfo}>
                    {t("Upload a voided check")}
                  </span>
                  <div className={styles.buttonsContainer}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleFileChange}
                    />
                    <Button onClick={handleUploadRequest} buttonText={t("Upload")} />
                    <Button
                      onClick={handleCancelRequest}
                      buttonText={t("Cancel Request")}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {achStatus === 3 && (
            <div className={styles.bankInfoContainer}>
              <img className={styles.icon} src={icon} alt="icon" />

              <div className={styles.accountInfo}>
                <span className={styles.accountName}>
                  {t("Bank Information Not Given")}
                </span>
              </div>
            </div>
          )}

          {showRadioButtons && (
            <div className={styles.accountInfo}>
              {achStatus !== 3 && (
                <span className={styles.accountNameInfo}>{accountName}</span>
              )}
              <span className={styles.newBankInfo}>{t("New Bank Information")}</span>
              <span className={styles.instructionsLine}>
                <span
                  className={styles.instructions}
                  onClick={handleDownloadInstructions}
                >
                  {t("Instructions")}
                </span>{" "}
                {t("for adding Bank Information")}
              </span>
              <div className={styles.radioButtonsContainer}>
                <RadioButton
                  value={t("U.S. Bank Account")}
                  isSelected={selectedOption === "U.S. Bank Account"}
                  onChange={() => handleOptionChange("U.S. Bank Account")}
                />
                <RadioButton
                  value={t("Canadian Bank Account")}
                  isSelected={selectedOption === "Canadian Bank Account"}
                  onChange={() => handleOptionChange("Canadian Bank Account")}
                />
              </div>
            </div>
          )}

          {!submittedBankInfo && (
            <form className={styles.bankForm} onSubmit={handleBankFormSubmit}>
              <input
                type="text"
                name="nameOnAccount"
                placeholder={t("Name on Bank Account")}
                value={bankAccountInfo.nameOnAccount}
                onChange={handleInputChange}
                onBlur={() => setNameTouched(true)}
                className={`${styles.input} ${
                  nameMismatchError ? styles.nameError : ""
                }`}
              />
              {nameTouched && bankAccountInfo.nameOnAccount !== accountName && (
                <div className={styles.nameMismatchError}>
                  {t("Name on Bank does not match", { accountName: accountName, bankAccountName: bankAccountInfo.nameOnAccount })}
                  <div className={styles.checkboxContainer}>
                    <div
                      className={`${styles.customCheckbox} ${
                        isOwner ? styles.checked : ""
                      }`}
                      onClick={handleCheckboxChange}
                    >
                      <span className={styles.checkmark}>&#x2715;</span>
                    </div>
                    <label className={styles.checkboxLabel}>
                      {t("I am the owner of this account")}
                    </label>
                  </div>
                </div>
              )}

              {/* Routing & account number for US */}
              {showUSBankForm ? (
                <div className={styles.bankFormRow}>
                  <input
                    type="text"
                    name="routingNumber"
                    placeholder={t("Routing Number")}
                    value={bankAccountInfo.routingNumber}
                    onChange={handleInputChange}
                    className={styles.input}
                  />
                  <input
                    type="text"
                    name="accountNumber"
                    placeholder={t("Account Number")}
                    value={bankAccountInfo.accountNumber}
                    onChange={handleInputChange}
                    className={styles.input}
                  />
                </div>
              ) : (
                <>
                  <div className={styles.bankFormRow}>
                    <input
                      type="text"
                      name="transitNumber"
                      placeholder={t("Transit Number")}
                      value={bankAccountInfo.transitNumber}
                      onChange={handleInputChange}
                      className={styles.input}
                    />
                    <input
                      type="text"
                      name="institutionNumber"
                      placeholder={t("Institution Number")}
                      value={bankAccountInfo.institutionNumber}
                      onChange={handleInputChange}
                      className={styles.input}
                    />
                  </div>
                  <div
                    className={`${styles.bankFormRow} ${styles.accountNumberRow}`}
                  >
                    <input
                      type="text"
                      name="accountNumber"
                      placeholder={t("Account Number")}
                      value={bankAccountInfo.accountNumber}
                      onChange={handleInputChange}
                      className={styles.input}
                    />
                  </div>
                </>
              )}

              <div className={styles.buttonsContainer}>
                <Button
                  buttonText={t("Next")}
                  className={
                    isNextButtonDisabled ? styles.disabled : styles.btnNext
                  }
                  disabled={isNextButtonDisabled}
                  onClick={handleNextClick}
                />
                <Button onClick={handleCancel} buttonText={t("Cancel")} />
              </div>
            </form>
          )}
        </>
        //   )}
        // </div>
      )}

      {submittedBankInfo && (
        <>
          {achStatus === 5 && reviewInProgress && (
            <>
              <p className={styles.inReview}>
                {t("Your account submission is in review")} <a href="mailto:amm@wwdb.com">amm@wwdb.com</a>.
              </p>
              <div className={styles.lastNext}>
                <Button
                  onClick={() =>
                    history.push(`/review-w9/${selectedItem.entityId}`)
                  }
                  buttonText={t("Next")}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default ReviewBankInfo;
